import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  createOrder, 
  updateOrder, 
  deleteOrder,
  pageOrders,
  getOrderDetail,
  getOrderByOrderNo,
  updateOrderResult,
  updateOrderStatus,
  updatePayStatus
} from '../../api/jadeIdentificationOrder';
import { message } from 'antd';

export const fetchOrderList = createAsyncThunk(
  'jadeIdentificationOrder/fetchList',
  async (queryDTO, { rejectWithValue }) => {
    try {
      const response = await pageOrders(queryDTO);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取订单列表失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchOrderDetail = createAsyncThunk(
  'jadeIdentificationOrder/fetchDetail',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getOrderDetail(orderId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取订单详情失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchOrderByOrderNo = createAsyncThunk(
  'jadeIdentificationOrder/fetchByOrderNo',
  async (orderNo, { rejectWithValue }) => {
    try {
      const response = await getOrderByOrderNo(orderNo);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取订单失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const createOrderAsync = createAsyncThunk(
  'jadeIdentificationOrder/create',
  async (order, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await createOrder(order);
      message.success('创建成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateOrderAsync = createAsyncThunk(
  'jadeIdentificationOrder/update',
  async (order, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateOrder(order);
      message.success('更新成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteOrderAsync = createAsyncThunk(
  'jadeIdentificationOrder/delete',
  async (orderId, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await deleteOrder(orderId);
      message.success('删除成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateOrderResultAsync = createAsyncThunk(
  'jadeIdentificationOrder/updateResult',
  async ({ orderId, result }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateOrderResult(orderId, result);
      message.success('更新鉴定结果成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新鉴定结果失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateOrderStatusAsync = createAsyncThunk(
  'jadeIdentificationOrder/updateStatus',
  async ({ orderId, status }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateOrderStatus(orderId, status);
      message.success('更新订单状态成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新订单状态失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updatePayStatusAsync = createAsyncThunk(
  'jadeIdentificationOrder/updatePayStatus',
  async ({ orderId, payStatus }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updatePayStatus(orderId, payStatus);
      message.success('更新支付状态成功');
      const state = getState().jadeIdentificationOrder;
      dispatch(fetchOrderList({
        page: state.page,
        pageSize: state.pageSize,
        ...state.queryParams
      }));
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新支付状态失败';
      message.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const jadeIdentificationOrderSlice = createSlice({
  name: 'jadeIdentificationOrder',
  initialState: {
    orderList: [],
    orderDetail: null,
    page: 1,
    pageSize: 10,
    total: 0,
    loading: false,
    queryParams: {},
    createStatus: {
      loading: false,
      success: false,
      error: null,
    },
    updateStatus: {
      loading: false,
      success: false,
      error: null,
    },
    deleteStatus: {
      loading: false,
      success: false,
      error: null,
    },
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setQueryParams: (state, action) => {
      state.queryParams = action.payload;
    },
    clearOrderDetail: (state) => {
      state.orderDetail = null;
    },
    clearCreateStatus: (state) => {
      state.createStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearUpdateStatus: (state) => {
      state.updateStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
    clearDeleteStatus: (state) => {
      state.deleteStatus = {
        loading: false,
        success: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderList.fulfilled, (state, action) => {
        state.loading = false;
        state.orderList = action.payload.records || action.payload.list || [];
        state.total = action.payload.total || 0;
      })
      .addCase(fetchOrderList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchOrderDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload;
      })
      .addCase(fetchOrderDetail.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchOrderByOrderNo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderByOrderNo.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload;
      })
      .addCase(fetchOrderByOrderNo.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createOrderAsync.pending, (state) => {
        state.createStatus.loading = true;
        state.createStatus.error = null;
      })
      .addCase(createOrderAsync.fulfilled, (state) => {
        state.createStatus.loading = false;
        state.createStatus.success = true;
      })
      .addCase(createOrderAsync.rejected, (state, action) => {
        state.createStatus.loading = false;
        state.createStatus.error = action.payload;
      })
      .addCase(updateOrderAsync.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.error = null;
      })
      .addCase(updateOrderAsync.fulfilled, (state) => {
        state.updateStatus.loading = false;
        state.updateStatus.success = true;
      })
      .addCase(updateOrderAsync.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })
      .addCase(deleteOrderAsync.pending, (state) => {
        state.deleteStatus.loading = true;
        state.deleteStatus.error = null;
      })
      .addCase(deleteOrderAsync.fulfilled, (state) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.success = true;
      })
      .addCase(deleteOrderAsync.rejected, (state, action) => {
        state.deleteStatus.loading = false;
        state.deleteStatus.error = action.payload;
      });
  },
});

export const {
  setPage,
  setPageSize,
  setQueryParams,
  clearOrderDetail,
  clearCreateStatus,
  clearUpdateStatus,
  clearDeleteStatus,
} = jadeIdentificationOrderSlice.actions;

export default jadeIdentificationOrderSlice.reducer;